import React, { useCallback, useMemo, useState, useEffect } from 'react'
import type { MilestoneType } from './types'
import { OverlayPanel, OverlayPanelProps } from 'primereact/overlaypanel'
import { DragEndEvent } from '@dnd-kit/core'
import { useHorizontalScrollUpdates, useInteractionEnabled, useWaveTimespans, useZoom } from './hooks'
import { useElementSize } from '@aytm/app/insights/dashboard/components/hooks'
import { dateDayCeil, dateDayFloor } from '@aytm/app/insights/dashboard/lib/datetime'
import { debounce } from '@aytm/app/insights/dashboard/lib/functions'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { timelineUISelector, timelineUIValidator, TimelineUISelectorParams } from './selectors'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { makePositionalTimelineUpdateEventAction } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'

export type TimelineUIContextShape = {
  isAllowedToShow: boolean
  isInteractionEnabled: boolean
  isDataUpdating: boolean
  // Zooming
  canZoomIn: boolean
  onZoomIn: () => void
  canZoomOut: boolean
  onZoomOut: () => void
  zoomFactor: number
  onSetZoomFactor: (value: number) => void
  onSubjectJumpRef: React.MutableRefObject<(arg0: { uid: string }) => void>
  // Scrollable container
  setContainerRef: React.LegacyRef<HTMLDivElement>
  negateContainerWidth: (key: number, width: number) => void
  containerWidth: number
  // Milestones CRUD
  currentMilestone: MilestoneType | null
  milestonesList: MilestoneType[] | null

  // Overlay
  currentOverlayType: 'list' | 'new' | 'edit' | 'delete'
  dismissOverlay(this: void): void
  // Create new milestone
  handleDragEnd: (event: DragEndEvent) => void
  markerOffset?: number

  minDateTimestamp: number
  maxDateTimestamp: number
  onMilestoneDeleteConfirm: (this: void, evt: React.MouseEvent<HTMLElement>) => void
  showDeleteMilestoneOverlay: (this: void, id: string, evt: React.MouseEvent<HTMLElement>) => void
  onWaveClick(this: void, id: string, evt: React.MouseEvent<HTMLDivElement>): void
  overlayProps: Partial<OverlayPanelProps>
  saveMilestone(this: void, entry: Partial<MilestoneType>): void
  setOverlayRef: React.LegacyRef<OverlayPanel>
  showEditMilestoneOverlay(this: void, id: string, evt: React.MouseEvent<HTMLElement>): void
  showMilestoneListOverlay(this: void, milestones: MilestoneType[], evt: React.MouseEvent<HTMLElement>): void
  showNewMilestoneOverlay(this: void, evt: React.MouseEvent<HTMLElement>): void
  validateMilestone(
    this: void,
    object: Partial<Pick<MilestoneType, 'title' | 'date' | 'id'>> & { parsedDate: Date },
    prev: Pick<MilestoneType, 'title' | 'date' | 'id'>
  ): { [key in keyof Partial<Pick<MilestoneType, 'title'> & { date: string }>]: string }
  lastRequestTimestamp: number

  scrollOffset: number
  setScrollingNodeRef: (instance: HTMLDivElement) => void
  resetTimeline: (blockWidth: number) => void
}

export const TimelineUIContext = React.createContext<TimelineUIContextShape>(null)

export const TimelineUIProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { isDataUpdating, isTimelineShown, initialZoom, initialOffset } = usePool<
    typeof InsightsDashboardPool,
    TimelineUISelectorParams,
    InsightsDashboardPoolState
  >(InsightsDashboardPool, timelineUISelector, timelineUIValidator)

  const { canZoomIn, onZoomIn, canZoomOut, onZoomOut, zoomFactor, onSetZoomFactor } = useZoom({
    initialZoom
  })

  const {
    setRef: setContainerRef,
    size: { width: rawContainerWidth }
  } = useElementSize()
  const [containerWidthNegation, setContainerWidthNegation] = useState<Map<number, number>>(new Map())
  const negateContainerWidth = useCallback((key: number, width: number) => {
    setContainerWidthNegation(state => (state.has(key) && state.get(key) === width ? state : state.set(key, width)))
  }, [])

  const containerWidth = useMemo(
    () => [...containerWidthNegation.values()].reduce((memo, item) => Math.max(0, memo - item), rawContainerWidth),
    [containerWidthNegation, rawContainerWidth]
  )

  const {
    minWavesBound: minDateTimestamp,
    maxWavesBound: maxDateTimestamp,
    milestones,
    saveMilestone,
    updateMilestone,
    deleteMilestone
  } = useWaveTimespans(containerWidth, zoomFactor)

  const { isAllowedToShow, isInteractionEnabled } = useInteractionEnabled(
    0 === minDateTimestamp && 0 === maxDateTimestamp
  )

  const { scrollOffset, setScrollingNodeRef } = useHorizontalScrollUpdates(zoomFactor * containerWidth, initialOffset)

  const debouncedZoomStateUpdater = useMemo(
    () =>
      debounce(250, () => {
        // So it seems that the store doesn't optimize mutation due to same-value assignment
        if ((initialZoom !== zoomFactor || initialOffset !== scrollOffset) && isTimelineShown) {
          poolDispatch(makePositionalTimelineUpdateEventAction(zoomFactor, scrollOffset))
        }
      }),
    [initialOffset, initialZoom, isTimelineShown, scrollOffset, zoomFactor]
  )

  useEffect(() => {
    debouncedZoomStateUpdater()
  }, [debouncedZoomStateUpdater])

  const [finalOffset, setFinalOffset] = useState<number>(null)
  const [lastRequestTimestamp, setLastRequestTimestamp] = useState<number>(0)

  const resetTimeline = useCallback(() => {
    setFinalOffset(null)
  }, [])

  const [currentOverlayType, setCurrentOverlayType] = React.useState<TimelineUIContextShape['currentOverlayType']>(null)

  const [milestonesList, setMilestonesList] = useState<MilestoneType[]>(null)
  const [milestoneWithMoreElement, setMilestoneWithMoreElement] = useState<HTMLElement>(null)
  const [currentMilestone, setCurrentMilestone] = useState<MilestoneType>(null)

  const [isOverlayShown, setIsOverlayShown] = React.useState(false)
  const overlayRef = React.useRef<OverlayPanel>(null)

  const setOverlayRef: React.LegacyRef<OverlayPanel> = node => {
    overlayRef.current = node
  }

  const handleOverlayHidden = () => {
    setIsOverlayShown(false)
    setFinalOffset(null)
  }

  const handleOverlayShown = () => {
    setIsOverlayShown(true)
  }

  const overlayProps = useMemo(
    () =>
      ({
        onShow: handleOverlayShown,
        onHide: handleOverlayHidden
      }) as Partial<OverlayPanelProps>,
    []
  )

  const dismissOverlay = useCallback(() => {
    overlayRef.current?.hide()
    setCurrentMilestone(null)
  }, [])

  const showOverlayForElement = useCallback(
    (element: HTMLElement) => {
      if (isOverlayShown) {
        overlayRef.current.hide()
        window.setTimeout(() => {
          overlayRef.current.show(null, element)
        }, 49)
      } else {
        overlayRef.current.show(null, element)
      }
    },
    [isOverlayShown]
  )

  const showMilestoneListOverlay = useCallback(
    (milestones: MilestoneType[], evt: React.MouseEvent<HTMLElement>) => {
      setFinalOffset(null)
      const label = (evt.target as HTMLElement).closest('label')
      if (isOverlayShown && milestoneWithMoreElement === label) {
        dismissOverlay()
        setMilestoneWithMoreElement(null)
        return
      }
      setMilestonesList(milestones)
      setCurrentOverlayType('list')

      showOverlayForElement(label)
      setMilestoneWithMoreElement(label)
    },
    [dismissOverlay, isOverlayShown, milestoneWithMoreElement, showOverlayForElement]
  )

  const showNewMilestoneOverlay = useCallback(
    (evt: React.MouseEvent<HTMLElement>, newMilestone?: MilestoneType) => {
      setCurrentMilestone(newMilestone ?? { id: null, title: '', offset: 0 })
      setCurrentOverlayType('new')
      setLastRequestTimestamp(Date.now())
      setMilestonesList(null)
      showOverlayForElement(evt.target as HTMLElement)
    },
    [showOverlayForElement]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const correction = 18
      const { delta, activatorEvent } = event
      const dragEndOffset = Math.min(containerWidth, Math.max(0, (delta?.x || 0) - correction))
      setFinalOffset(dragEndOffset + correction / 2)
      const percentageAcross = (dragEndOffset + scrollOffset) / (containerWidth * zoomFactor)
      const target = (activatorEvent.target as HTMLElement).closest('.milestone-picker').querySelector('.icon')
      showNewMilestoneOverlay({ target } as unknown as React.MouseEvent<HTMLElement>, {
        id: null,
        title: '',
        offset: percentageAcross
      })
    },
    [containerWidth, scrollOffset, showNewMilestoneOverlay, zoomFactor]
  )

  const showEditMilestoneOverlay = useCallback(
    (id: string, evt: React.MouseEvent<HTMLElement>) => {
      setFinalOffset(null)
      if (isOverlayShown) {
        dismissOverlay()
      }
      const milestone = milestones.find(m => m.id === id)
      setCurrentMilestone(milestone)

      const isSamePlace = milestonesList && milestone && milestonesList.some(m => m.id === milestone.id)
      setCurrentOverlayType('edit')
      setLastRequestTimestamp(Date.now())
      const positionAtElement = isSamePlace ? milestoneWithMoreElement : (evt.target as HTMLElement).closest('label')
      setMilestoneWithMoreElement(positionAtElement)
      showOverlayForElement(positionAtElement)
      !isSamePlace && setMilestonesList(null)
    },
    [isOverlayShown, milestones, milestonesList, milestoneWithMoreElement, showOverlayForElement, dismissOverlay]
  )

  const showDeleteMilestoneOverlay = useCallback(
    (id: string, evt: React.MouseEvent<HTMLElement>) => {
      evt.preventDefault()
      const milestone = milestones.find(m => m.id === id)
      setCurrentMilestone(milestone)
      setCurrentOverlayType('delete')
    },
    [milestones]
  )

  const onMilestoneDeleteConfirm = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      evt.preventDefault()
      deleteMilestone(currentMilestone?.id)
      setCurrentMilestone(null)
      dismissOverlay()
    },
    [deleteMilestone, currentMilestone?.id, dismissOverlay]
  )

  const onSubjectJumpRef = React.useRef<(arg0: { uid: string }) => void>(null)

  const handleWaveClick = useCallback((uid: string, evt: React.MouseEvent<HTMLDivElement>): void => {
    evt.preventDefault()
    onSubjectJumpRef.current?.({ uid })
  }, [])

  const validateMilestone = useCallback<TimelineUIContextShape['validateMilestone']>(
    (object, prev) => {
      const result = {} as { [key in keyof Partial<Pick<MilestoneType, 'title'> & { date: string }>]: string }
      const isNew = null == object.id
      if (!object.title) {
        result.title = 'Title is required'
      } else if (object.title.length < 3) {
        result.title = 'Title is too short'
      } else if (object.title.length >= 50) {
        result.title = 'Title exceeds the limit'
      } else if (isNew && milestones.some(m => m.title === object.title)) {
        result.title = 'Title already exists'
      } else if (!isNew && prev.title !== object.title && milestones.some(m => m.title === object.title)) {
        result.title = 'Title already taken'
      }
      if (!object.date) {
        result.date = 'Date is required'
      } else {
        const date = object.parsedDate // new Date(object.date)
        if (isNaN(date.getTime())) {
          result.date = 'Invalid date'
        } else if (
          date.getTime() < dateDayFloor(minDateTimestamp).getTime() ||
          date.getTime() > dateDayCeil(maxDateTimestamp).getTime()
        ) {
          result.date = 'Date is out of range'
        }
      }
      return result
    },
    [maxDateTimestamp, milestones, minDateTimestamp]
  )

  const handleSaveMilestone = useCallback(
    (entry: Partial<MilestoneType>) => {
      if (!isInteractionEnabled) {
        return
      }
      if (entry.id) {
        updateMilestone(entry as MilestoneType)
      } else {
        saveMilestone(entry)
      }
    },
    [isInteractionEnabled, saveMilestone, updateMilestone]
  )

  const milestonesUIContext = useMemo(() => {
    return {
      isAllowedToShow,
      isInteractionEnabled,
      isDataUpdating,
      onSubjectJumpRef,
      setContainerRef,
      containerWidth,
      negateContainerWidth,
      currentMilestone,
      currentOverlayType,
      dismissOverlay,
      handleDragEnd,
      markerOffset: finalOffset,
      maxDateTimestamp,
      minDateTimestamp,
      milestonesList,
      showDeleteMilestoneOverlay,
      onMilestoneDeleteConfirm,
      onWaveClick: handleWaveClick,
      overlayProps,
      saveMilestone: handleSaveMilestone,
      setOverlayRef,
      showEditMilestoneOverlay,
      showMilestoneListOverlay,
      showNewMilestoneOverlay,
      validateMilestone,
      canZoomIn,
      onZoomIn,
      canZoomOut,
      onZoomOut,
      zoomFactor,
      onSetZoomFactor,
      lastRequestTimestamp,
      scrollOffset,
      setScrollingNodeRef,
      resetTimeline
    }
  }, [
    isAllowedToShow,
    isInteractionEnabled,
    isDataUpdating,
    setContainerRef,
    containerWidth,
    negateContainerWidth,
    currentMilestone,
    currentOverlayType,
    dismissOverlay,
    handleDragEnd,
    finalOffset,
    maxDateTimestamp,
    minDateTimestamp,
    milestonesList,
    showDeleteMilestoneOverlay,
    onMilestoneDeleteConfirm,
    handleWaveClick,
    overlayProps,
    handleSaveMilestone,
    showEditMilestoneOverlay,
    showMilestoneListOverlay,
    showNewMilestoneOverlay,
    validateMilestone,
    canZoomIn,
    onZoomIn,
    canZoomOut,
    onZoomOut,
    zoomFactor,
    onSetZoomFactor,
    lastRequestTimestamp,
    scrollOffset,
    setScrollingNodeRef,
    resetTimeline
  ])

  return <TimelineUIContext.Provider value={milestonesUIContext}>{children}</TimelineUIContext.Provider>
}

export const useTimelineUIContext = (onJump?: (arg0: { uid: string }) => void) => {
  const context = React.useContext(TimelineUIContext)
  if (!context) {
    throw new Error('useTimelineUIContext must be used within a <TimelineUIProvider />')
  }
  if (Boolean(onJump) && !(context.onSubjectJumpRef?.current === onJump)) {
    context.onSubjectJumpRef.current = onJump
  }
  return context
}
